import { DateTime } from "luxon";

import { Activity } from "@/models/diabetes/ActivityModel.ts";
import { Food } from "@/models/diabetes/FoodModel.ts";
import { Glycemia } from "@/models/diabetes/GlycemiaModel.ts";
import { Insulin } from "@/models/diabetes/InsulinModel.ts";
import { Report } from "@/models/diabetes/ReportModel.ts";
import { CGMGlycemiaParameters } from "@/models/DiabetesDataModel.ts";
import { InsulinBasalRate } from "@/models/InsulinBasalRate.ts";
import {
  ClosedLoopEvents,
  ClosedLoopPumpModeChangeEvent,
  ClosedLoopUserModeChangeEvent,
  PumpEvent,
} from "@/models/InsulinPumpEvents.ts";
import { DateUtils, SupportedDate, toDateTime } from "@/utils/dateUtils.ts";

type SplittableData = { id: string; date: string };

export type DayData<T extends SplittableData> = {
  lastOfPreviousDay: T | undefined;
  data: T[];
  firstOfNextDay: T | undefined;
  date: string;
};

export type CGMDailyGraphData = {
  date: string;
  parameters: CGMGlycemiaParameters;
  glycemia: DayData<Glycemia>;
  insulin: DayData<Insulin>;
  insulinBasalRates: DayData<InsulinBasalRate>;
  food: DayData<Food>;
  activity: DayData<Activity>;
  reports: DayData<Report>;
  pumpEvents: DayData<PumpEvent>;
  closedLoopEvents: {
    userMode: DayData<ClosedLoopUserModeChangeEvent>;
    pumpMode: DayData<ClosedLoopPumpModeChangeEvent>;
  };
};

const emptyDayData = <T extends SplittableData>(date: string): DayData<T> => ({
  lastOfPreviousDay: undefined,
  data: [],
  firstOfNextDay: undefined,
  date,
});

const splitIntoDayData = <T extends SplittableData>(startDate: DateTime, data: T[]): Record<string, DayData<T>> => {
  const result: Record<string, DayData<T>> = {};
  // Split into days
  data.forEach(item => {
    const itemKey = _get_day_key(startDate, item.date);
    if (!(itemKey in result)) {
      result[itemKey] = emptyDayData(itemKey);
    }
    result[itemKey].data.push(item);
  });
  // Find lastOfPreviousDay and firstOfNextDay
  Object.values(result).forEach(item => {
    const previousDayKey = _get_previous_day_key(item.date);
    if (previousDayKey && previousDayKey in result) {
      item.lastOfPreviousDay = result[previousDayKey].data[0];
    }
    const nextDayKey = _get_next_day_key(item.date);
    if (nextDayKey && nextDayKey in result) {
      item.firstOfNextDay = result[nextDayKey].data[result[nextDayKey].data.length - 1];
    }
  });

  return result;
};

/**
 * Split the diabetes data by day
 *
 * Used to display diabetes data in the daily graph

 */
export const makeCGMDailyGraphData = (
  from: DateTime,
  to: DateTime,
  parameters: CGMGlycemiaParameters,
  glycemia: Glycemia[],
  insulin: Insulin[],
  food: Food[] = [],
  activity: Activity[] = [],
  reports: Report[] = [],
  pumpEvents: PumpEvent[] = [],
  insulinBasalRates: InsulinBasalRate[] = [],
  closedLoopEvents: ClosedLoopEvents = {
    userMode: [],
    pumpMode: [],
  },
): CGMDailyGraphData[] => {
  const start = from.startOf("day");
  const end = to.endOf("day");
  if (!start.isValid || !end.isValid) {
    throw new Error("Invalid date");
  }

  // Create a map of all days between the start and end date
  const dayMap: Record<string, CGMDailyGraphData> = {};
  let index = 0;
  let day: DateTime<true> = start.plus({ days: index });
  const glycemiaMap = splitIntoDayData(start, glycemia);
  const insulinMap = splitIntoDayData(start, insulin);
  const insulinBasalRatesMap = splitIntoDayData(start, insulinBasalRates);
  const foodMap = splitIntoDayData(start, food);
  const activityMap = splitIntoDayData(start, activity);
  const reportsMap = splitIntoDayData(start, reports);
  const pumpEventsMap = splitIntoDayData(start, pumpEvents);
  const closedLoopEventsUserModeMap = splitIntoDayData(start, closedLoopEvents.userMode);
  const closedLoopEventsPumpModeMap = splitIntoDayData(start, closedLoopEvents.pumpMode);

  while (day <= end) {
    // Each day has a date, a copy of the parameters and empty arrays for the data
    // The date is repeated as the key for the dayMap
    const key = day.toISODate();
    dayMap[key] = {
      date: key,
      parameters,
      glycemia: key in glycemiaMap ? glycemiaMap[key] : emptyDayData(key),
      insulin: key in insulinMap ? insulinMap[key] : emptyDayData(key),
      food: key in foodMap ? foodMap[key] : emptyDayData(key),
      activity: key in activityMap ? activityMap[key] : emptyDayData(key),
      reports: key in reportsMap ? reportsMap[key] : emptyDayData(key),
      pumpEvents: key in pumpEventsMap ? pumpEventsMap[key] : emptyDayData(key),
      insulinBasalRates: key in insulinBasalRatesMap ? insulinBasalRatesMap[key] : emptyDayData(key),
      closedLoopEvents: {
        userMode: key in closedLoopEventsUserModeMap ? closedLoopEventsUserModeMap[key] : emptyDayData(key),
        pumpMode: key in closedLoopEventsPumpModeMap ? closedLoopEventsPumpModeMap[key] : emptyDayData(key),
      },
    };
    index += 1;
    day = start.plus({ days: index });
  }

  // Turn the map into an array and sort it by date
  return Object.values(dayMap).sort((a, b) => DateUtils.compareDates(b.date, a.date));
};

/**
 * Get the number of days between two dates and add it to the start date
 * Then turn it into a string
 * This is the key for the dayMap
 * Used by makeCGMDailyGraphData to get the day key for an item
 * @param startDate The start date of the data
 * @param date The date of the item
 */
const _get_day_key = (startDate: DateTime, date: SupportedDate) => {
  const index = DateUtils.compareDates(date, startDate);
  const day: DateTime<true> = startDate.plus({ days: index });
  return day.toISODate();
};

const _get_previous_day_key = (date: string) => toDateTime(date).minus({ days: 1 }).toISODate();

const _get_next_day_key = (date: string) => toDateTime(date).plus({ days: 1 }).toISODate();
